// Pure rule → plant-node attachment via the tag map run in reverse
// (construct.R7 · docs/plans/plant-model-construction.md § 8.2).

import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import type { TagMapping } from '@/versions/v1/schema/tag-mapping';
import { DIAGNOSTIC_RULES } from '@/versions/v1/shared/bunker-hill-seed';
import { ADVISORY_RULES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { subprocessFmeaLabel } from '@/versions/v1/shared/bunker-hill-plant-nodes';
import { TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';

/** Map each `when` clause's signal role → TagMapping.subprocessId. Prose-only
 * rules (no `when`) attach to no node. */
export function ruleNodeIds(
  rule: DiagnosticRule,
  tagMapping: readonly TagMapping[] = TAG_MAPPING,
): readonly string[] {
  if (!rule.when?.length) return [];
  const ids = new Set<string>();
  for (const clause of rule.when) {
    const mapping = tagMapping.find((row) => row.role === clause.signal);
    if (mapping?.subprocessId) ids.add(mapping.subprocessId);
  }
  return [...ids];
}

export function ruleById(ruleId: string): DiagnosticRule | undefined {
  return (
    DIAGNOSTIC_RULES.find((row) => row.id === ruleId) ??
    ADVISORY_RULES.find((row) => row.id === ruleId)
  );
}

export function rulesForNode(
  nodeId: string,
  rules: readonly DiagnosticRule[],
  tagMapping: readonly TagMapping[] = TAG_MAPPING,
): DiagnosticRule[] {
  return rules.filter((rule) => ruleNodeIds(rule, tagMapping).includes(nodeId));
}

export function proseOnlyRules(rules: readonly DiagnosticRule[]): DiagnosticRule[] {
  return rules.filter((rule) => !rule.when?.length);
}

export type FmeaAttachmentSummary = {
  ruleCount: number;
  nodeCount: number;
  nodeLines: readonly { nodeId: string; nodeLabel: string; ruleLabels: readonly string[] }[];
  proseOnlyCount: number;
};

function subprocessLabel(nodeId: string): string {
  return subprocessFmeaLabel(nodeId);
}

/** De-duplicate rule ids from claim rows — one rule id counts once (construct.R7). */
export function uniqueRuleIds(ruleIds: readonly string[]): string[] {
  return [...new Set(ruleIds)];
}

/** Summarise workbook rule claims attached to nodes for the FMEA contribution card. */
export function fmeaAttachmentSummary(
  ruleIds: readonly string[],
  tagMapping: readonly TagMapping[] = TAG_MAPPING,
): FmeaAttachmentSummary {
  const rules = uniqueRuleIds(ruleIds)
    .map((id) => ruleById(id))
    .filter((row): row is DiagnosticRule => row != null);
  const attached = rules.filter((rule) => ruleNodeIds(rule, tagMapping).length > 0);
  const proseOnly = proseOnlyRules(rules);

  const byNode = new Map<string, DiagnosticRule[]>();
  for (const rule of attached) {
    for (const nodeId of ruleNodeIds(rule, tagMapping)) {
      const list = byNode.get(nodeId) ?? [];
      list.push(rule);
      byNode.set(nodeId, list);
    }
  }

  const nodeLines = [...byNode.entries()].map(([nodeId, nodeRules]) => ({
    nodeId,
    nodeLabel: subprocessLabel(nodeId),
    ruleLabels: nodeRules.map((rule) => rule.diagnosis),
  }));

  return {
    ruleCount: attached.length,
    nodeCount: byNode.size,
    nodeLines,
    proseOnlyCount: proseOnly.length,
  };
}
